<div class="modal-header border-0 align-items-center px-4 pt-4 pb-2">
    <h5 class="modal-title text-primary d-flex align-items-center gap-3 m-0">
        <div [class]="'confirmation-icon rounded-3 d-inline-flex align-items-center justify-content-center ' + (config?.iconContainerClass || '')"
            style="width: 44px; height: 44px;">
            <i [class]="'fs-4 ' + (config?.iconClass || '')"></i>
        </div>
        <span class="fs-5 fw-semibold">
            {{ config?.title }}
        </span>
    </h5>
    <button type="button" class="btn-close" aria-label="Close" [disabled]="isProcessing" (click)="onCancel()">
    </button>
</div>

<hr class="my-2 mx-4">

<div class="modal-body py-4 px-4">

    <p class="op-intro text-secondary text-center mx-auto mb-4">
        {{ config?.message }}
    </p>

    <!-- ============ OPERATION CONSOLE ============ -->
    <div class="op-grid">

        <!-- ---------- Bank account context ---------- -->
        <section class="op-context">
            <div class="op-card">
                <div class="op-card-head context-head">
                    <i class="ri-bank-line"></i>
                    <h6 class="mb-0">Bank Account Details</h6>
                </div>
                <div class="op-card-body">
                    <div class="d-flex align-items-center justify-content-between gap-3 mb-3">
                        <div class="text-start">
                            <span class="field-label">Account Name</span>
                            <p class="fw-bold text-dark fs-5 mb-0">{{ config?.bankAccountDetails?.accountName }}</p>
                        </div>
                        <div class="d-flex flex-column align-items-end">
                            <img [src]="config?.bankAccountDetails?.bankLogoUrl" alt="bank logo"
                                class="bank-logo mb-1">
                            <div class="text-end small text-secondary bank-name">
                                {{ config?.bankAccountDetails?.bankName }}
                            </div>
                        </div>
                    </div>

                    <hr class="my-3 opacity-25">

                    <div class="detail-row">
                        <span class="text-secondary">Account ID</span>
                        <span class="fw-semibold text-dark">{{ config?.bankAccountDetails?.accountId }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="text-secondary">Alias</span>
                        <span class="fw-semibold text-dark">{{ config?.bankAccountDetails?.alias }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="text-secondary">Account Number</span>
                        <span class="fw-semibold text-dark">{{ config?.bankAccountDetails?.accountNumber | numberMaskPipe:4 }}</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- ---------- Right: operation scope ---------- -->
        @if (isWideLayout) {
            <section class="op-scope">

                <!-- Optional Date Range -->
                @if (config?.dateRange && dateRangeForm) {
                    <div [formGroup]="dateRangeForm" class="op-card">
                        <div class="op-card-head scope-head">
                            <i class="ri-calendar-event-line"></i>
                            <h6 class="mb-0">{{ config?.dateRange?.title || 'Date Range (Optional)' }}</h6>
                            @if (dateRangeForm.value.fromDate || dateRangeForm.value.toDate) {
                                <button type="button" class="op-clear ms-auto" (click)="clearDateRange()">
                                    <i class="ri-eraser-line"></i> Clear
                                </button>
                            }
                        </div>
                        <div class="op-card-body">
                            <p class="op-help">
                                {{ config?.dateRange?.helper || 'Leave empty to sync with default range, or pick a custom From / To window.' }}
                            </p>

                            <div class="d-flex flex-column flex-sm-row gap-2">
                                <!-- Date From -->
                                <div class="input-group input-group-md">
                                    <input class="form-control flatpickr-input" type="text"
                                        mwlFlatpickr
                                        [options]="flatpickrFromOptions"
                                        [dateFormat]="'Y-m-d'"
                                        formControlName="fromDate"
                                        placeholder="{{'COMMON.FROM' | translate}}">
                                    @if (dateRangeForm.value.fromDate) {
                                        <span [class.rest-icon-ltr]="_translate.getCurrentLang() === 'en'"
                                            [class.rest-icon-rtl]="_translate.getCurrentLang() !== 'en'"
                                            class="rest-icon"
                                            (click)="clearDateControl('fromDate')">
                                            <i class="ri-close-circle-fill"></i>
                                        </span>
                                    }
                                    <div class="input-group-text text-primary">
                                        <i class="ri-calendar-2-line"></i>
                                    </div>
                                </div>

                                <!-- Date To -->
                                <div class="input-group input-group-md">
                                    <input class="form-control flatpickr-input" type="text"
                                        mwlFlatpickr
                                        [options]="flatpickrToOptions"
                                        [dateFormat]="'Y-m-d'"
                                        formControlName="toDate"
                                        placeholder="{{'COMMON.TO' | translate}}">
                                    @if (dateRangeForm.value.toDate) {
                                        <span [class.rest-icon-ltr]="_translate.getCurrentLang() === 'en'"
                                            [class.rest-icon-rtl]="_translate.getCurrentLang() !== 'en'"
                                            class="rest-icon"
                                            (click)="clearDateControl('toDate')">
                                            <i class="ri-close-circle-fill"></i>
                                        </span>
                                    }
                                    <div class="input-group-text text-primary">
                                        <i class="ri-calendar-2-line"></i>
                                    </div>
                                </div>
                            </div>

                            @if (dateRangeError) {
                                <div class="op-alert warning mt-3">
                                    <i class="ri-error-warning-line"></i>
                                    <small>{{ dateRangeError }}</small>
                                </div>
                            }
                        </div>
                    </div>
                }

                <!-- Optional Terminal Selection -->
                @if (config?.terminalSelection && terminalForm) {
                    <div [formGroup]="terminalForm" class="op-card">
                        <div class="op-card-head scope-head">
                            <i class="ri-bank-card-2-line"></i>
                            <h6 class="mb-0">{{ config?.terminalSelection?.title || 'Terminals' }}</h6>
                            @if (terminalMode === 'specific' && terminalForm.get('terminalIds').value?.length) {
                                <span class="op-count ms-auto">
                                    {{ terminalForm.get('terminalIds').value.length }} selected
                                </span>
                            }
                        </div>
                        <div class="op-card-body">
                            <p class="op-help">
                                {{ config?.terminalSelection?.helper || 'Sync all terminals, or choose specific ones to sync only those.' }}
                            </p>

                            <!-- Segmented All / Specific toggle -->
                            <div class="seg-toggle" role="group" aria-label="Terminal scope">
                                <input class="btn-check" type="radio" id="terminalModeAll"
                                    value="all" formControlName="mode"
                                    (change)="onTerminalModeChange('all')">
                                <label class="seg-btn" for="terminalModeAll">
                                    <i class="ri-stack-line"></i> All Terminals
                                </label>

                                <input class="btn-check" type="radio" id="terminalModeSpecific"
                                    value="specific" formControlName="mode"
                                    (change)="onTerminalModeChange('specific')">
                                <label class="seg-btn" for="terminalModeSpecific">
                                    <i class="ri-list-check-2"></i> Specific
                                </label>
                            </div>

                            <!-- Smooth reveal (grid-row 0fr → 1fr) -->
                            <div class="term-reveal" [class.open]="terminalMode === 'specific'">
                                <div class="term-reveal-inner">
                                    @if (terminalLoadError) {
                                        <div class="op-alert danger">
                                            <i class="ri-error-warning-line"></i>
                                            <small>Failed to load terminals. Please try again.</small>
                                        </div>
                                    } @else {
                                        <div class="form-floating">
                                            <ng-select formControlName="terminalIds"
                                                [items]="terminalOptions"
                                                bindLabel="terminalId"
                                                bindValue="terminalId"
                                                [multiple]="true"
                                                [closeOnSelect]="false"
                                                [loading]="isLoadingTerminals"
                                                [readonly]="isTerminalSelectDisabled"
                                                [class.has-value]="terminalForm.get('terminalIds').value?.length">
                                            </ng-select>
                                            <label class="form-label">Terminals</label>
                                        </div>

                                        @if (hasNoTerminals) {
                                            <div class="op-alert warning mt-3">
                                                <i class="ri-information-line"></i>
                                                <small>No POS terminals are linked to this bank account.</small>
                                            </div>
                                        } @else if (terminalSelectionError) {
                                            <div class="op-alert warning mt-3">
                                                <i class="ri-error-warning-line"></i>
                                                <small>Please select at least one terminal, or switch to "All Terminals".</small>
                                            </div>
                                        }
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </section>
        }
    </div>

    <!-- ============ CONFIRMATION GATE ============ -->
    <div [formGroup]="confirmationForm" class="confirm-gate">
        <div class="gate-head">
            <i class="ri-shield-keyhole-line"></i>
            <span>Type "<strong>{{ expectedConfirmationText }}</strong>" to proceed</span>
        </div>
        <input type="text" id="bankAccountActionConfirmationInput" formControlName="confirmationInput"
            class="form-control gate-input"
            [class.is-invalid]="showValidationError"
            [placeholder]="expectedConfirmationText" />
        @if (showValidationError) {
            <div class="invalid-feedback d-block">
                Please type the confirmation text exactly as shown to proceed.
            </div>
        }
    </div>
</div>

<div class="modal-footer py-3 bg-light justify-content-end">
    <button (click)="onCancel()" type="button" class="btn btn-secondary" [disabled]="isProcessing">
        Cancel
    </button>
    <button (click)="onConfirm()"
        [disabled]="isProcessing || !isReadyToConfirm"
        type="button" [class]="config?.confirmButtonClass || 'btn btn-primary px-32'">
        @if (isProcessing) {
        <div class="d-flex align-items-center">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <span>Loading...</span>
        </div>
        } @else {
        {{ config?.confirmButtonLabel }}
        }
    </button>
</div>
